import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import productData from "../Datas/productData";

export const CartContext = createContext(null);

const readStorageArray = (key) => {
  if (typeof window === "undefined") return [];

  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normaliseOption = (value) => {
  if (typeof value === "string") return value;
  return value?.name || "";
};

const normaliseQuantity = (quantity) => {
  const parsed = Number.parseInt(quantity, 10);
  return Number.isFinite(parsed) ? Math.min(10, Math.max(1, parsed)) : 1;
};

const normaliseCart = (items) =>
  items
    .filter((item) => item && Number.isInteger(Number(item.id)))
    .map((item) => ({
      id: Number(item.id),
      size: normaliseOption(item.size),
      color: normaliseOption(item.color),
      quantity: normaliseQuantity(item.quantity),
    }));

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    normaliseCart(readStorageArray("jeautyfern-cart"))
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    readStorageArray("jeautyfern-wishlist")
      .map(Number)
      .filter((id) => Number.isInteger(id))
  );

  useEffect(() => {
    window.localStorage.setItem("jeautyfern-cart", JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    window.localStorage.setItem(
      "jeautyfern-wishlist",
      JSON.stringify(wishlistItems)
    );
  }, [wishlistItems]);

  const addToCart = useCallback((productId, quantity = 1, size = "", color = "") => {
    const id = Number(productId?.id ?? productId);
    if (!productData.some((item) => item.id === id)) return;

    const selectedSize = normaliseOption(size);
    const selectedColor = normaliseOption(color);
    const amount = normaliseQuantity(quantity);

    setCartItems((current) => {
      const index = current.findIndex(
        (item) =>
          item.id === id &&
          item.size === selectedSize &&
          item.color === selectedColor
      );

      if (index === -1) {
        return [
          ...current,
          { id, size: selectedSize, color: selectedColor, quantity: amount },
        ];
      }

      return current.map((item, itemIndex) =>
        itemIndex === index
          ? { ...item, quantity: Math.min(10, item.quantity + amount) }
          : item
      );
    });
  }, []);

  const removeFromCart = useCallback((productId, size = "", color = "") => {
    const id = Number(productId?.id ?? productId);
    const selectedSize = normaliseOption(size);
    const selectedColor = normaliseOption(color);

    setCartItems((current) =>
      current.filter(
        (item) =>
          !(
            item.id === id &&
            item.size === selectedSize &&
            item.color === selectedColor
          )
      )
    );
  }, []);

  const updateQuantity = useCallback(
    (productId, size = "", color = "", quantity = 1) => {
      const id = Number(productId?.id ?? productId);
      const selectedSize = normaliseOption(size);
      const selectedColor = normaliseOption(color);
      const nextQuantity = Number.parseInt(quantity, 10);

      if (!Number.isFinite(nextQuantity) || nextQuantity <= 0) {
        removeFromCart(id, selectedSize, selectedColor);
        return;
      }

      setCartItems((current) =>
        current.map((item) =>
          item.id === id &&
          item.size === selectedSize &&
          item.color === selectedColor
            ? { ...item, quantity: Math.min(10, nextQuantity) }
            : item
        )
      );
    },
    [removeFromCart]
  );

  const clearCart = useCallback(() => setCartItems([]), []);

  const addToWishlist = useCallback((productId) => {
    const id = Number(productId?.id ?? productId);
    if (!productData.some((item) => item.id === id)) return;
    setWishlistItems((current) =>
      current.includes(id) ? current : [...current, id]
    );
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    const id = Number(productId?.id ?? productId);
    setWishlistItems((current) => current.filter((itemId) => itemId !== id));
  }, []);

  const toggleWishlist = useCallback((productId) => {
    const id = Number(productId?.id ?? productId);
    if (!productData.some((item) => item.id === id)) return;
    setWishlistItems((current) =>
      current.includes(id)
        ? current.filter((itemId) => itemId !== id)
        : [...current, id]
    );
  }, []);

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const cartSubtotal = useMemo(
    () =>
      cartItems.reduce((total, item) => {
        const found = productData.find((productItem) => productItem.id === item.id);
        return total + (found ? found.price * item.quantity : 0);
      }, 0),
    [cartItems]
  );

  const value = useMemo(
    () => ({
      cartItems,
      cartCount,
      cartSubtotal,
      addToCart,
      addCart: addToCart,
      removeFromCart,
      removeCart: removeFromCart,
      updateQuantity,
      clearCart,
      wishlistItems,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isWishlisted: (productId) => wishlistItems.includes(Number(productId)),
    }),
    [
      addToCart,
      addToWishlist,
      cartCount,
      cartItems,
      cartSubtotal,
      clearCart,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlistItems,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used inside CartProvider");
  return context;
};
